import { ReactNode, useRef } from 'react';
import { A11y, Autoplay, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Box, IconButton } from '@chakra-ui/react';
import { ChevronRightIcon } from '@udacity/chakra-uds-icons';

import { ChevronLeftIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/mousewheel';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

/**
 * Dedicated testimonials carousel: two cards per slide on desktop, one on mobile
 * (Figma 658-21844 / 659-22142). Intentionally separate from `FadeCardCarousel` —
 * that one is a one-up fade carousel whose homepage caller fires
 * `hero_banner_viewed` per slide, which is the wrong taxonomy here.
 */
export function GovernmentTestimonialsCarousel<
  T extends {
    key: string;
  },
>({ items, renderCard }: { items: T[]; renderCard: ({ item, index }: { item: T; index: number }) => ReactNode }) {
  const isClientReady = useClientRender();
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const { i18n, isRtl } = useI18n();

  // Note: hold the initial paint to a single slide so Swiper can hydrate without a layout flash.
  const slides = isClientReady ? items : items.slice(0, 1);

  return (
    <Box position='relative'>
      <Box
        isolation='isolate'
        position='relative'
        sx={{
          '.swiper': {
            opacity: isClientReady ? 1 : 0,
            height: isClientReady ? undefined : '0px',
            paddingBottom: '48px',
          },
          '.swiper-slide': {
            height: 'auto',
            display: 'flex',
          },
          '.swiper-slide > *': {
            width: '100%',
          },
          '.swiper-pagination': {
            height: '30px',
          },
          '.swiper-pagination-bullet': {
            backgroundColor: 'blue.300',
            width: '16px',
            borderRadius: '8px',
            height: '4px',
            opacity: 0.33,
            transition: 'all 0.3s ease',
          },
          '.swiper-pagination-bullet-active': {
            backgroundColor: 'blue.300',
            opacity: 1,
            width: '56px',
            borderRadius: '8px',
            height: '4px',
          },
        }}
      >
        <Swiper
          key={isClientReady ? 'client' : 'server'}
          direction='horizontal'
          // Only loop once the full slide list is mounted. The first paint
          // renders a single slide (see `slides` above), and Swiper warns /
          // misbehaves if loop mode initializes without enough slides for
          // `slidesPerView`. The Swiper remounts via `key` when this flips.
          loop={isClientReady}
          modules={[A11y, Autoplay, Mousewheel, Navigation, Pagination]}
          slidesPerView={1}
          spaceBetween={0}
          autoplay={{
            delay: 5000,
            // Stop auto-advancing once the visitor swipes or uses the nav arrows.
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          }}
          breakpoints={{
            // 768px === Chakra `md`; desktop shows two cards per slide with a 32px gap
            // (Figma 658-21844). Mobile stays at spaceBetween 0 so the full-bleed card
            // (`mx: -16px` / `width: calc(100% + 32px)`) reaches both viewport edges with
            // no trailing right-side strip.
            768: {
              slidesPerView: 2,
              spaceBetween: 32,
            },
          }}
          mousewheel={{
            forceToAxis: true,
            releaseOnEdges: true,
            thresholdDelta: 10,
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          pagination={{
            clickable: true,
          }}
          onBeforeInit={(swiper) => {
            swiper.changeLanguageDirection(i18n.dir());
            // The arrow refs are null on first render (the IconButtons mount
            // after Swiper in the tree), so the `navigation` prop above can't
            // see them. Reassign here, once the refs are attached, so the
            // desktop prev/next controls actually drive the carousel.
            if (typeof swiper.params.navigation !== 'boolean' && swiper.params.navigation) {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
          }}
        >
          {slides.map((item, index) => (
            <SwiperSlide key={item.key}>{renderCard({ item, index })}</SwiperSlide>
          ))}
        </Swiper>
      </Box>
      <IconButton
        ref={prevRef}
        aria-label='Previous'
        color='grey.500'
        icon={isRtl ? <ChevronRightIcon height='36px' width='36px' /> : <ChevronLeftIcon height='36px' width='36px' />}
        isDisabled={!isClientReady}
        left='-64px'
        opacity={0.5}
        position='absolute'
        size='md'
        top='calc(50% - 48px)'
        variant='ghost'
        display={{
          base: 'none',
          xl: 'inline-flex',
        }}
      />
      <IconButton
        ref={nextRef}
        aria-label='Next'
        color='grey.500'
        icon={isRtl ? <ChevronLeftIcon height='36px' width='36px' /> : <ChevronRightIcon height='36px' width='36px' />}
        isDisabled={!isClientReady}
        opacity={0.5}
        position='absolute'
        right='-64px'
        size='md'
        top='calc(50% - 48px)'
        variant='ghost'
        display={{
          base: 'none',
          xl: 'inline-flex',
        }}
      />
    </Box>
  );
}
